
import { format, parseISO, differenceInMinutes, differenceInHours, isBefore } from "date-fns";
import { Trip, FlightDetails, TripElement } from "@/types/trip";

export interface FlightNode {
  code: string;
  date: Date;
  type: 'departure' | 'arrival';
  flightIndex: number;
}

export interface AirportStay {
  code: string;
  arrivals: {
    date: Date;
    flightIndex: number;
  }[];
  departures: {
    date: Date;
    flightIndex: number;
  }[];
  totalStayHours: number;
}

export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
};

export const calculateFlightDuration = (departureTime: string, arrivalTime: string): string => {
  try {
    const departureDate = parseISO(departureTime || "1970");
    const arrivalDate = parseISO(arrivalTime || "1970");
    
    const durationInMinutes = differenceInMinutes(arrivalDate, departureDate);
    
    const absoluteDurationInMinutes = Math.abs(durationInMinutes);
    
    return formatDuration(absoluteDurationInMinutes);
  } catch (error) {
    console.error('Error calculating flight duration:', error);
    return '00:00';
  }
};

export const identifyRealDestination = (elements: Trip['elements']): string => {
  if (!elements || elements.length === 0) return '';
  
  // Only analyze flights - remove hotel analysis
  const flightElements = elements.filter(e => e.type === 'flight').sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  if (flightElements.length === 0) return '';
  if (flightElements.length === 1) {
    return (flightElements[0].details as FlightDetails).arrival.code;
  }
  
  // Continue with the existing flight sequence analysis
  const sequence: FlightNode[] = [];
  flightElements.forEach((flight, index) => {
    const details = flight.details as FlightDetails;
    sequence.push({
      code: details.departure.code,
      date: parseISO(details.departure.time || "1970"),
      type: 'departure',
      flightIndex: index
    });
    sequence.push({
      code: details.arrival.code,
      date: parseISO(details.arrival.time || "1970"),
      type: 'arrival',
      flightIndex: index
    });
  });
  sequence.sort((a, b) => a.date.getTime() - b.date.getTime());
  console.debug('🧠 Flight sequence:', sequence.map(node => `${node.type === 'departure' ? 'FROM' : 'TO'} ${node.code} at ${format(node.date, 'MMM d, yyyy HH:mm')}`));
  
  // Continue with the rest of the flight analysis logic
  const airportFrequency: Record<string, number> = {};
  sequence.forEach(node => {
    airportFrequency[node.code] = (airportFrequency[node.code] || 0) + 1;
  });
  console.debug('🧠 Airport frequencies:', airportFrequency);
  const airportStays: Record<string, AirportStay> = {};
  Object.keys(airportFrequency).forEach(code => {
    airportStays[code] = {
      code,
      arrivals: [],
      departures: [],
      totalStayHours: 0
    };
  });
  sequence.forEach(node => {
    if (node.type === 'arrival') {
      airportStays[node.code].arrivals.push({
        date: node.date,
        flightIndex: node.flightIndex
      });
    } else {
      airportStays[node.code].departures.push({
        date: node.date,
        flightIndex: node.flightIndex
      });
    }
  });
  Object.values(airportStays).forEach(stay => {
    stay.arrivals.forEach(arrival => {
      const nextDeparture = stay.departures.find(dep => dep.date.getTime() > arrival.date.getTime());
      if (nextDeparture) {
        const hoursStayed = differenceInHours(nextDeparture.date, arrival.date);
        stay.totalStayHours += hoursStayed;
      }
    });
  });
  console.debug('🧠 Airport stays analysis:', airportStays);
  const firstAirport = sequence[0].code;
  const lastAirport = sequence[sequence.length - 1].code;
  if (firstAirport === lastAirport) {
    console.debug('🧠 Detected round trip starting and ending at:', firstAirport);
    const candidateDestinations = Object.values(airportStays).filter(stay => stay.code !== firstAirport).filter(stay => stay.totalStayHours > 5);
    if (candidateDestinations.length > 0) {
      candidateDestinations.sort((a, b) => b.totalStayHours - a.totalStayHours);
      const realDestination = candidateDestinations[0];
      console.debug('🧠 Identified destination by longest stay:', realDestination.code, 'with', realDestination.totalStayHours, 'hours');
      return realDestination.code;
    }
  }
  const singleVisitAirports = Object.entries(airportFrequency).filter(([_, count]) => count === 1).map(([code]) => code);
  if (singleVisitAirports.length > 0) {
    const lastSingleVisitAirport = singleVisitAirports.map(code => ({
      code,
      position: sequence.findIndex(node => node.code === code)
    })).sort((a, b) => b.position - a.position)[0];
    if (lastSingleVisitAirport) {
      console.debug('🧠 Identified potential one-way destination:', lastSingleVisitAirport.code);
      return lastSingleVisitAirport.code;
    }
  }
  const midPointIndex = Math.floor(flightElements.length / 2);
  const midPointDestination = (flightElements[midPointIndex].details as FlightDetails).arrival.code;
  console.debug('🧠 Using fallback midpoint destination:', midPointDestination);
  return midPointDestination;
};

export const isValidUUID = (uuid: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
};
